import * as XLSX from 'xlsx';
import moment from 'moment';

// Shared parsing/formatting helpers for the Excel-import "Finance Posting"
// pages (JV Import, Lease Invoice Import, Lease Receipt Import). These three
// backends all use the same conventions — strict .NET DateTime/number fields,
// a {status,data,message} response envelope — so the fixes here apply to all
// of them at once instead of being re-debugged per page.

// Excel stores dates as serial numbers when the cell is formatted as a date;
// fall back to parsing plain text dates otherwise. Returns a JS Date (or null)
// so it can both be displayed (DD-MMM-YYYY) and serialized (ISO) correctly —
// the API's date fields are strict .NET DateTime, not display strings.
export const parseExcelDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (parsed) {
            return new Date(parsed.y, parsed.m - 1, parsed.d);
        }
    }
    const asMoment = moment(value, ['DD-MMM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', moment.ISO_8601], true);
    return asMoment.isValid() ? asMoment.toDate() : null;
};

// Blank Excel cells come through as '' — treat those as "no amount" rather than 0.
export const parseExcelNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

// Excel cells that look numeric (ref numbers, account codes, cost centers) come
// back as JS numbers from the sheet reader — the API's string fields are strict
// (System.Text.Json won't coerce a JSON number into a C# string), so every
// "text" column must be forced to an actual string before it's sent.
export const toText = (value) => (value === null || value === undefined ? '' : String(value));

export const dateColumnFormatter = (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : '');
export const amountColumnFormatter = (params) => (params.value === null || params.value === undefined ? '' : Number(params.value).toFixed(2));

// Unwrap whatever envelope the API used for a list response — this backend's
// "general" controllers wrap responses as { status, data, message } but aren't
// strongly typed in Swagger, so try every plausible shape defensively.
export const extractRows = (res) => {
    const data = res?.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        const candidateKeys = ['result', 'Result', 'rows', 'Rows', 'data', 'Data', 'items', 'Items', 'groups', 'Groups'];
        for (const key of candidateKeys) {
            if (Array.isArray(data[key])) return data[key];
        }
        // Single-key wrapper we didn't anticipate, e.g. { importGroups: [...] } — use it if it's the only array on the object.
        const arrayValues = Object.values(data).filter(Array.isArray);
        if (arrayValues.length === 1) return arrayValues[0];
    }
    return [];
};

// Import-group items may come back as plain strings/numbers or as {text,value}
// objects in either camelCase or PascalCase — normalize to {label, value}.
export const toOption = (g) => {
    if (g === null || typeof g !== 'object') return { label: toText(g), value: g };
    const label = g.text ?? g.Text ?? g.label ?? g.Label ?? g.name ?? g.Name ?? g.importGroup ?? g.ImportGroup ?? g.value ?? g.Value;
    const value = g.value ?? g.Value ?? g.importGroup ?? g.ImportGroup ?? g.text ?? g.Text;
    return { label: toText(label), value };
};

// This API family can return HTTP 200 with status:false for a business-logic
// failure (axios only throws for non-2xx), so a successful POST/DELETE isn't
// enough on its own — check the envelope's status flag too.
export const isEnvelopeFailure = (res) => res?.data?.status === false;

// TEMP diagnostic — shared across all three import pages so whichever one is
// having trouble, the browser console shows exactly what came back and what
// ended up in the dropdown, instead of guessing blind.
export const logImportGroupsFetch = (label, res, groups, options) => {
    // eslint-disable-next-line no-console
    console.log(`[${label}] getimportgroups raw response:`, res?.data);
    // eslint-disable-next-line no-console
    console.log(`[${label}] extracted groups:`, groups);
    // eslint-disable-next-line no-console
    console.log(`[${label}] dropdown options:`, options);
};
